import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Reveal } from "./Reveal";

const FAQ_ITEMS = [
    {
        question: "Dois-je avancer de l'argent ?",
        answer: (
            <p>
                Aucun frais à avancer : votre assurance nous règle directement après le remplacement
                du pare-brise.
            </p>
        ),
    },
    {
        question: "Jusqu'à 450 € offerts ?",
        answer: (
            <>
                <p>Le montant du cadeau offert est déterminé en fonction de deux éléments :</p>
                <ul className="mt-3 space-y-1.5 list-none">
                    <li className="flex gap-2.5"><span className="text-gold">—</span> le tarif du pare-brise remplacé</li>
                    <li className="flex gap-2.5"><span className="text-gold">—</span> le montant de la franchise fixé par votre assurance</li>
                </ul>
            </>
        ),
    },
    {
        question: "Quels documents dois-je fournir ?",
        answer: (
            <ul className="space-y-1.5 list-none">
                <li className="flex gap-2.5"><span className="text-gold">—</span> Carte grise</li>
                <li className="flex gap-2.5"><span className="text-gold">—</span> Mémo d'assuré (ou attestation)</li>
                <li className="flex gap-2.5"><span className="text-gold">—</span> Numéro de téléphone &amp; e-mail</li>
            </ul>
        ),
    },
    {
        question: "Quelle garantie ?",
        answer: <p>Garantie à vie sur la pose et l'étanchéité du pare-brise.</p>,
    },
];

const FaqItem = ({ item, index, open, onToggle }) => (
    <div
        data-testid={`faq-item-${index}`}
        className={`rounded-2xl border transition-[border-color,background-color] duration-500 ${
            open ? "border-gold/50 bg-white/[0.04]" : "border-white/10 bg-white/[0.02] hover:border-gold/30"
        }`}
    >
        <button
            data-testid={`faq-question-${index}`}
            onClick={onToggle}
            aria-expanded={open}
            className="w-full flex items-center gap-5 px-6 sm:px-8 py-6 text-left"
        >
            <span
                className={`flex items-center justify-center w-10 h-10 shrink-0 rounded-full font-display font-bold text-lg transition-colors duration-500 ${
                    open ? "bg-gradient-to-br from-gold-light to-gold-dark text-midnight-900" : "border border-gold/50 text-gold"
                }`}
            >
                {index + 1}
            </span>
            <span className="flex-1 font-display font-semibold text-lg sm:text-xl text-white">
                {item.question}
            </span>
            <motion.span
                animate={{ rotate: open ? 180 : 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="text-gold shrink-0"
            >
                <ChevronDown size={20} strokeWidth={2.5} />
            </motion.span>
        </button>
        <AnimatePresence initial={false}>
            {open && (
                <motion.div
                    data-testid={`faq-answer-${index}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                >
                    <div className="px-6 sm:px-8 pb-7 pl-[5.25rem] sm:pl-[5.75rem] text-sm sm:text-base text-gray-300 leading-relaxed">
                        {item.answer}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

export const Faq = () => {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section id="faq" data-testid="faq-section" className="relative py-28 scroll-mt-20 bg-midnight-800 diagonal-gold-lines border-y border-white/5">
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
                <Reveal className="text-center">
                    <h2 className="font-display font-bold text-6xl sm:text-7xl tracking-tight text-gold-gradient gold-shimmer">
                        FAQ
                    </h2>
                    <span className="mt-6 mx-auto block h-px w-24 bg-gradient-to-r from-transparent via-gold to-transparent" />
                </Reveal>

                <div className="mt-14 space-y-4">
                    {FAQ_ITEMS.map((item, i) => (
                        <Reveal key={item.question} delay={i * 0.08}>
                            <FaqItem
                                item={item}
                                index={i}
                                open={openIndex === i}
                                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                            />
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};
